import { useQuery } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export interface CalendarEntry {
  title?: string | null;
  assigned_to: string | null;
  clinician: { id: string; name: string } | null;
  created_at: string | null;
  description?: string;
  encounter_date: string;
  encounter_type: string | null;
  id: string;
  imaging_study_id?: string;
  location: string | null;
  patient: { id: string; mrn: string | null; name: string | null } | null;
  patient_id: string | null;
  scheduled_at: string | null;
  /** The instant rendered in the practice's timezone. Derived server-side. */
  scheduled_local_date: string | null;
  scheduled_local_time: string | null;
  status: string;
}

export interface CalendarWindow {
  complete_through: string | null;
  date_from: string;
  date_to: string;
  entries: CalendarEntry[];
  time_zone: string | null;
  total: number;
  /** True when the window held more encounters than the API returns at once. */
  truncated: boolean;
}

/**
 * One window of the calendar.
 *
 * Bounds, not a month: the 42-cell grid shape is the client's, and sending
 * explicit dates means a week view later needs no backend change. The caller
 * derives them with `monthRange()`.
 */
export function useCalendar(params: { assigned_to?: string; date_from: string; date_to: string }, enabled = true) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && enabled,
    queryKey: ['calendar', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<CalendarWindow>(`/api/v1/organizations/${orgId}/encounters/calendar`, {
        params,
      });
      return data;
    },
  });
}
